import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";
import { usePageMeta } from "@/hooks/usePageMeta";
import { INDUSTRIES } from "@/data/industries";
import { SERVICES } from "@/data/services";

export default function IndustryPage() {
  const { slug } = useParams();
  const index = INDUSTRIES.findIndex((i) => i.slug === slug);
  const industry = index !== -1 ? INDUSTRIES[index] : null;
  const next = INDUSTRIES[(index + 1) % INDUSTRIES.length];

  usePageMeta({
    title: industry
      ? `${industry.name} Marketing & Branding | BrandZenith`
      : "Industries | BrandZenith",
    description: industry ? industry.pitch : "Industries BrandZenith serves.",
    path: `/industries/${slug}`,
  });

  if (!industry) return <Navigate to="/" replace />;

  const words = industry.name.split(" ");
  const accent = words[words.length - 1];
  const title = words.slice(0, -1).join(" ");

  return (
    <>
      <Navbar />
      <main data-testid="industry-page">
        <PageHeader
          overline="Industries We Serve"
          title={title}
          accent={accent}
          copy={industry.pitch}
        />

        <section className="relative pb-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Reveal>
              <div
                data-testid="industry-artwork"
                className="relative aspect-[16/9] sm:aspect-[21/9] rounded-lg overflow-hidden border border-white/[0.08] mb-20 lg:mb-28"
              >
                <img
                  src={`/images/industry-${industry.slug}.webp`}
                  alt={`${industry.name} — BrandZenith industry artwork`}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div
                  aria-hidden="true"
                  className="absolute inset-0 bg-gradient-to-t from-[#050507]/80 via-transparent to-[#050507]/20"
                />
                <span className="absolute bottom-5 left-6 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.24em] text-[#E8B6FF]/90 bg-black/50 backdrop-blur-sm border border-[#8B2CFF]/30 rounded px-3 py-1.5">
                  BrandZenith × {industry.name}
                </span>
              </div>
            </Reveal>

            <div className="mb-24 lg:mb-32">
              <Reveal>
                <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#8B2CFF] mb-6">
                  Where We Help
                </p>
                <h2 className="font-display text-2xl sm:text-4xl font-extrabold uppercase tracking-tight leading-[1.1] mb-12">
                  Built for {industry.name.toLowerCase()}{" "}
                  <span className="text-gradient-purple">like yours.</span>
                </h2>
              </Reveal>
              <div className="grid sm:grid-cols-2 gap-5">
                {industry.points.map((pt, i) => (
                  <Reveal key={pt} delay={i * 0.08} className="h-full">
                    <div
                      data-testid={`industry-point-${i}`}
                      className="h-full glass-card rounded-lg p-7 transition-[border-color,box-shadow] duration-500 hover:border-[#6D00FF]/40 hover:shadow-[0_0_36px_-12px_rgba(109,0,255,0.5)]"
                    >
                      <span className="font-display text-2xl font-extrabold tracking-tighter text-gradient-purple">
                        0{i + 1}
                      </span>
                      <p className="mt-4 text-sm sm:text-base text-[#A6A6B0] leading-relaxed">
                        {pt}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            <div className="mb-24">
              <Reveal>
                <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#8B2CFF] mb-4">
                  The Full Studio
                </p>
                <h2 className="font-display text-2xl sm:text-4xl font-extrabold uppercase tracking-tight leading-[1.1] mb-5">
                  Every service, <span className="text-gradient-purple">one partner.</span>
                </h2>
                <p className="text-sm sm:text-base text-[#A6A6B0] max-w-xl mb-12">
                  Everything below is available for {industry.name.toLowerCase()} — mixed and
                  matched into one growth system.
                </p>
              </Reveal>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {SERVICES.map((s, i) => (
                  <Reveal key={s.slug} delay={(i % 3) * 0.06} y={16}>
                    <Link
                      to={`/services/${s.slug}`}
                      data-testid={`industry-all-service-${i}`}
                      className="group flex items-center gap-4 glass-card rounded-lg px-5 py-4 transition-[border-color,box-shadow] duration-500 hover:border-[#6D00FF]/40 hover:shadow-[0_0_28px_-10px_rgba(109,0,255,0.5)]"
                    >
                      <span className="font-display text-[11px] font-bold tracking-[0.18em] text-[#8B2CFF]/70 group-hover:text-[#C43CFF] transition-colors duration-300">
                        {s.n}
                      </span>
                      <span className="flex-1 font-display text-sm font-bold uppercase tracking-tight text-white/80 group-hover:text-white transition-colors duration-300">
                        {s.title}
                      </span>
                      <ArrowRight className="w-3.5 h-3.5 shrink-0 text-white/20 group-hover:text-[#C43CFF] transition-[color,transform] duration-300 group-hover:translate-x-1" />
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-6 border-t border-white/[0.06] pt-8">
              <Link
                to="/"
                data-testid="industry-back-link"
                className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#A6A6B0] hover:text-white transition-colors duration-300"
              >
                <ArrowLeft className="w-4 h-4" /> All Industries
              </Link>
              <Link
                to={`/industries/${next.slug}`}
                data-testid="industry-next-link"
                className="group text-right"
              >
                <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-[#A6A6B0]/60 mb-1">
                  Next Industry
                </span>
                <span className="font-display text-lg sm:text-xl font-extrabold uppercase tracking-tight text-white/80 group-hover:text-gradient-purple transition-colors duration-300 flex items-center gap-3">
                  {next.name} <ArrowRight className="w-5 h-5 text-[#8B2CFF] transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </>
  );
}
